import { LockOutlined, UserOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Form,
  Input,
  Space,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import { usePreferences } from "../hooks/usePreferences";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useNotifier } from "../providers/NotificationProvider";
import { AuthCard } from "../components/AuthCard";
import { isLocalHost } from "../lib/http";

function Login() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const notifier = useNotifier();
  const { direction } = usePreferences();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const savedUsername = localStorage.getItem("juleb-username") ?? "";
  const savedDb = localStorage.getItem("juleb-db") ?? "";
  const [rememberMe, setRememberMe] = useState(!!savedUsername);
  const isReturningUser = !!localStorage.getItem("juleb-visited");

  useEffect(() => {
    if (savedUsername) {
      form.setFieldValue("username", savedUsername);
    }
    if (savedDb) {
      form.setFieldValue("db", savedDb);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirect to the target app, preserving the redirect query param and hash
  // (Odoo uses hash-based routing).
  const redirectToApp = () => {
    const redirect = searchParams.get("redirect") || "/web";
    const redirectUrl = redirect.startsWith("/") ? redirect : `/${redirect}`;
    const hash = window.location.hash;
    const fullRedirectUrl = hash ? `${redirectUrl}${hash}` : redirectUrl;
    window.location.href = fullRedirectUrl;
  };

  const completeLogin = (sessionId?: string) => {
    localStorage.setItem("juleb-visited", "1");
    if (sessionId) {
      localStorage.setItem("session", sessionId);
    }
    redirectToApp();
  };

  const login = (credentials: {
    username: string;
    password: string;
    db?: string;
  }) => {
    setLoading(true);
    return axios
      .post(
        "/auth_graphql/login",
        {
          username: credentials.username,
          password: credentials.password,
          db: isLocalHost() ? credentials.db : undefined,
        },
        { withCredentials: true },
      )
      .finally(() => setLoading(false));
  };

  const onFinish = async (values: any) => {
    try {
      const response = await login({
        username: values.username,
        password: values.password,
        db: values.db,
      });
      const sessionId = response.data;
      if (sessionId) {
        if (rememberMe) {
          localStorage.setItem("juleb-username", values.username);
          localStorage.setItem("juleb-db", values.db || "");
        } else {
          localStorage.removeItem("juleb-username");
          localStorage.removeItem("juleb-db");
        }
        completeLogin(sessionId);
      }
    } catch (error) {
      notifier.error({
        message: t("auth.login.error"),
        description: t("auth.login.errorDescription"),
      });
    }
  };

  // Carry the redirect target across the reset detour so the user still lands on
  // the app they were heading for. The typed username travels in router state
  // rather than the query string — it is an email address.
  const goToForgotPassword = () => {
    const redirect = searchParams.get("redirect");
    navigate(
      {
        pathname: "/forgot-password",
        search: redirect ? `?redirect=${encodeURIComponent(redirect)}` : "",
      },
      {
        state: {
          username: form.getFieldValue("username"),
          db: form.getFieldValue("db"),
        },
      },
    );
  };

  return (
    <AuthCard
      title={
        isReturningUser ? t("auth.login.title") : t("auth.login.titleNew")
      }
      subtitle={t("auth.login.subtitle")}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        size="large"
        onFinish={onFinish}
      >
        {isLocalHost() && (
          <Form.Item label={t("auth.login.db")} name="db">
            <Input
              placeholder={t("auth.login.dbPlaceholder")}
              dir={direction === "rtl" ? "rtl" : "ltr"}
            />
          </Form.Item>
        )}

        <Form.Item label={t("auth.login.username")} name="username">
          <Input
            prefix={<UserOutlined />}
            placeholder={t("auth.login.usernamePlaceholder")}
            dir={direction === "rtl" ? "rtl" : "ltr"}
          />
        </Form.Item>

        <Form.Item label={t("auth.login.password")} name="password">
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={t("auth.login.passwordPlaceholder")}
            dir={direction === "rtl" ? "rtl" : "ltr"}
          />
        </Form.Item>

        <Flex align="center" justify="space-between" gap={12} wrap>
          <Checkbox
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
          >
            {t("auth.login.rememberMe")}
          </Checkbox>
          <Button
            type="link"
            style={{ paddingInline: 0 }}
            onClick={goToForgotPassword}
          >
            {t("auth.login.forgotPassword")}
          </Button>
        </Flex>

        <Button
          loading={loading}
          disabled={loading}
          type="primary"
          htmlType="submit"
          block
          size="large"
          style={{ marginTop: 24 }}
        >
          {t("auth.login.submit")}
        </Button>
      </Form>

      <Divider style={{ margin: 0 }} />

      <Space direction="vertical" size={2}>
        <Typography.Text strong>{t("auth.login.helpTitle")}</Typography.Text>
        <Typography.Text type="secondary">
          {t("auth.login.helpText")}
        </Typography.Text>
      </Space>
    </AuthCard>
  );
}

export default Login;
